import React, { useState } from 'react';
import {
    View,
    Text,
    TouchableOpacity,
    Image
} from 'react-native-web';
import moment from 'moment';
import { getTimeZone } from 'react-native-localize'

import GLOBAL from '../configs/config_global';
import { ChatHtmlView } from './component_chat_html_view';
import Lightbox from 'yet-another-react-lightbox';
import Captions from 'yet-another-react-lightbox/plugins/captions';

export function ChatItem({
    item = {},
    isInDetail = false,
    containerWidth = 0,
    onPress = () => {}
}) {
    // region Derived Data
    const photoSize = 128;
    const displayingPhotoList = item['images'] || [];
    const hasPhotos = displayingPhotoList.length > 0;
    const remainingPhotoCount = hasPhotos ? (displayingPhotoList.length - 3) : 0;
    const hasNewComment = Boolean(item['has_new_comments']);
    const commentBadgeColor = hasNewComment ? GLOBAL.COLOR.BASIC.GREEN_HEX : GLOBAL.COLOR.BASIC.BLACK_TWO;
    const isNewTopic = !item['is_read'];
    const hasManyCities = item['serving_city']?.length > 2;
    const topicCreatedAtMoment = moment.utc(item['created_at']).tz(getTimeZone());
    // endregion

    // region Local State
    const [photoCarouselOpened, setPhotoCarouselOpened] = useState(false);
    const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);
    // endregion

    // region Render City View
    const renderPhotoView = () => (
        hasPhotos &&
        <View style={{ width: '100%', height: photoSize, flexDirection: 'row', marginTop: 24 }}>
            {
                displayingPhotoList
                    .filter((_, index) => index <= 2)
                    .map((photo, index) => (
                        <TouchableOpacity
                            key={`photoItem_${item.id}_${index}`}
                            onPress={() => {
                                setCurrentPhotoIndex(index);
                                setPhotoCarouselOpened(true);
                            }}
                            style={{ width: photoSize, height: photoSize, backgroundColor: GLOBAL.COLOR.BACKGROUND_GREY, marginRight: 8, borderRadius: 8, overflow: 'hidden' }}>
                            <Image
                                key={item.key}
                                source={{ uri: photo.path }}
                                style={{ flex: 1 }}
                                resizeMode={'cover'}
                            />

                            {/* Last Item Overlay */}
                            {
                                ((index === 2) && (remainingPhotoCount > 0)) &&
                                <View style={{ position: 'absolute', left: 0, right: 0, bottom: 0, top: 0, backgroundColor: 'rgba(0, 0, 0, 0.5)', alignItems: 'center', justifyContent: 'center' }}>
                                    <Text style={{ color: 'white', fontWeight: '500', fontSize: 32 }}>
                                        +{remainingPhotoCount}
                                    </Text>
                                </View>
                            }
                        </TouchableOpacity>
                    ))
            }
        </View>
    )
    // endregion

    // region Render City Tag
    const renderCityTag = (cityName) => (
        <View key={`cityTag_${cityName}`} style={{ paddingHorizontal: 8, paddingVertical: 6, borderRadius: 6, backgroundColor: GLOBAL.COLOR.BACKGROUND_GREY, marginRight: 4, marginTop: 4 }}>
            <Text style={{ fontWeight: '500', color: GLOBAL.COLOR.BASIC.BLACK_TWO, fontSize: 14, letterSpacing: 0.4 }}>
                {cityName}
            </Text>
        </View>
    )
    // endregion

    // region Render City and Comment View
    const renderCityAndCommentView = () => (
        <View style={{ flexDirection: 'row', flexWrap: 'wrap', alignItems: 'center', flex: 1, marginTop: 24 }}>
            {/* City List */}
            {
                item['serving_city'] ?
                    ((item['serving_city'].length === Object.keys(GLOBAL.CITIES).length)) ? renderCityTag('ALL CITIES') : item['serving_city'].filter((_, index) => isInDetail ? true : (index < 2)).map((city) => renderCityTag(city.name.toUpperCase()))
                    :
                    renderCityTag('DENVER')
            }
            {(hasManyCities && !isInDetail) ? renderCityTag(`+${item['serving_city']?.length - 2}`) : null}

            {/* Blank Space */}
            <View style={{ flex: 1 }} />

            {/* New Comments Badge */}
            {
                (hasNewComment && !isInDetail) &&
                <View style={{ marginRight: 8, borderRadius: 16, paddingVertical: 4, paddingHorizontal: 10, backgroundColor: GLOBAL.COLOR.BASIC.GREEN_HEX }}>
                    <Text style={{ fontWeight: 'bold', color: 'white', fontSize: 14 }}>
                        New Replies
                    </Text>
                </View>
            }

            {
                !isInDetail &&
                <Image
                    source={require('../images/iconLineChat@3x.png')}
                    style={{ width: 18, height: 18, tintColor: commentBadgeColor }}
                    resizeMode={'contain'}
                />
            }

            {
                !isInDetail &&
                <Text style={{ fontWeight: '500', fontSize: 16, marginLeft: 8, color: commentBadgeColor }}>
                    {item['comment_count']}
                </Text>
            }
        </View>
    )
    // endregion

    // region Render Main Content
    const renderMainContent = () => (
        <View styles={{ width: '100%', backgroundColor: 'white', borderBottomWidth: 1, borderColor: GLOBAL.COLOR.DIVIDER }}>
            {/* Author */}
            <View style={{ flexDirection: 'row', alignItems: 'flex-start' }}>
                {/* User Avatar */}
                <Image
                    source={item.author?.avatar ? { uri: item.author?.avatar } : require('../images/image_user_placeholder_round.png')}
                    style={{ width: 40, height: 40, borderRadius: 20, marginRight: 16 }}
                    resizeMode={item.author?.avatar ? 'cover' : 'contain'}
                />

                {/* Name and Posted Date */}
                <View style={{ flex: 1 }}>
                    <Text style={{ fontWeight: '450' }}>
                        {item.author?.name}{item.author?.['portal_username']?.trim() ? ` - ${item.author?.['portal_username']}` : ''}
                    </Text>

                    <Text style={{ color: GLOBAL.COLOR.BASIC.NEUTRAL_GREY, fontSize: 12, marginTop: 6 }}>
                        {item['created_at'] ? ((moment().unix() - topicCreatedAtMoment.unix()) > (86400 * 3) ? topicCreatedAtMoment.format('MMM D, YYYY') : topicCreatedAtMoment.fromNow()) : 'Some time ago'}
                    </Text>
                </View>

                {/* Fill In Needed */}
                {
                    (Number(item['fill_in_needed']) === 1) &&
                    <View style={{ backgroundColor: item['is_filled'] ? GLOBAL.COLOR.BASIC.UI_GREEN : ((moment().isSameOrBefore(moment.utc(item['event_date'])) || item['is_filled']) ? GLOBAL.COLOR.BASIC.UI_YELLOW : GLOBAL.COLOR.BASIC.COVER_GRAY), paddingHorizontal: 10, paddingVertical: 6, borderRadius: 6 }}>
                        <Text style={{ fontWeight: '500', color: 'white', fontSize: 14, letterSpacing: 0.4 }}>
                            {item['is_filled'] ? 'FILLED' : 'FILL IN NEEDED'}
                        </Text>
                    </View>
                }
            </View>

            {/* Title */}
            <View style={{ flexDirection: 'row', alignItems: 'center', alignSelf: 'stretch', marginTop: 24 }}>
                {/* Unread Badge */}
                {
                    (isNewTopic && !isInDetail) &&
                    <View style={{ marginRight: 8, borderRadius: 16, paddingVertical: 4, paddingHorizontal: 10, backgroundColor: GLOBAL.COLOR.BASIC.GREEN_HEX }}>
                        <Text style={{ fontWeight: 'bold', color: 'white', fontSize: 14 }}>
                            New
                        </Text>
                    </View>
                }

                {/* Title */}
                <Text style={{ flex: 1, fontWeight: 'bold', fontSize: 18, marginTop: 1 }} numberOfLines={isInDetail ? null : 1}>
                    {item.title}
                </Text>
            </View>

            {/* Event Date */}
            {
                (Number(item['fill_in_needed']) === 1 && !item['is_filled'] && (item['event_date']?.trim() ? moment().isSameOrBefore(moment.utc(item['event_date'])) : false)) &&
                <View style={{ backgroundColor: 'rgba(250, 57, 61, 0.1)', paddingHorizontal: 12, paddingVertical: 8, borderRadius: 6, alignSelf: 'flex-start', marginVertical: 16 }}>
                    <Text style={{ fontWeight: 'bold', color: GLOBAL.COLOR.MAIN, fontSize: 15, letterSpacing: 0.4 }}>
                        Event {isInDetail ? 'Time' : 'Date'}: {moment.utc(item['event_date']).tz(getTimeZone()).format('MMM DD, YYYY' + (isInDetail ? ' - h:mm A' : ''))}
                    </Text>
                </View>
            }

            {/* Description */}
            <ChatHtmlView item={item} containerWidth={containerWidth} isInDetail={isInDetail} />

            {/* Photo */}
            {renderPhotoView()}

            {/* Posted Cities and Number of Replies */}
            {renderCityAndCommentView()}
        </View>
    )
    // endregion

    // region MAIN RENDER
    return(
        <View style={{ alignSelf: 'stretch', padding: 24 }}>
            {/* Main Content */}
            {renderMainContent()}

            {/* Light Box */}
            <Lightbox
                plugins={[Captions]}
                open={photoCarouselOpened}
                close={() => setPhotoCarouselOpened(false)}
                index={currentPhotoIndex}
                on={{ view: ({ index }) => setCurrentPhotoIndex(index) }}
                slides={displayingPhotoList.map((photo, index) => ({
                    key: `imageItem_${item.id}`,
                    src: photo['path_webp'] || photo['path'],
                    id: photo.id,
                    title: `Photo ${index + 1} of ${displayingPhotoList.length}`
                }))}
                carousel={{ imageFit: 'contain' }}
                styles={{ container: { backgroundColor: 'rgba(0, 0, 0, 0.8)' } }}
            />
        </View>
    )
    // endregion
}
